import { inDtoToDocumentInsertEntity } from 'serviceWorker/repository/document/documentInDtoToEntity';
import { IssueEntity } from 'serviceWorker/repository/issue/entity';
import { issueDtoToEntityToSave } from 'serviceWorker/repository/issue/issueDtoToEntity';
import { mergeLinkedDocumentationItems } from 'shared/domain/issue/linkedDocumentation';
import { DocumentInDto } from 'shared/dtos/in/document';
import { IssueInDto } from 'shared/dtos/in/issue';
import { get as getCompanies } from '../companies/companies';
import { get as getContracts } from '../contracts/contracts';
import { get as getCorrectiveActionTypes } from '../fieldValues/correctiveActionTypes/correctiveActionTypes';
import {
  removeBatchByParentId as removeDocumentsBatch,
  removeSettledBatchByParentType as removeSettledDocumentsBatchByType,
  upsertBatch as upsertDocumentsBatch,
} from '../documents';
import { entityDbClearFactory } from '../entityDbClearFactory';
import { get as getEnvironmentalAspects } from '../fieldValues/environmentalAspects/environmentalAspects';
import { get as getHazardCategories } from '../fieldValues/hazardCategories/hazardCategories';
import { get as getIssueVerificationItems } from '../fieldValues/issueVerificationItems/issueVerificationItems';
import { get as getDelayReasons } from '../fieldValues/delayReasons/delayReasons';
import { get as getRejectionReasons } from '../fieldValues/rejectionReasons/rejectionReasons';
import { get as getRootCausesFromDb } from '../fieldValues/rootCauses/rootCauses';
import { get as getEffectsFromDb } from '../fieldValues/effects/effects';
import { get as getCircumstancesFromDb } from '../fieldValues/circumstances/circumstances';
import { get as getImpactsFromDb } from '../fieldValues/impacts/impacts';
import { get as getPrioritiesFromDb } from '../fieldValues/priorities/priorities';
import { get as getProjectStagesFromDb } from '../fieldValues/projectStages/projectStages';
import { get as getDecisionsToImposeFineFromDb } from '../fieldValues/decisionsToImposeFine/decisionsToImposeFine';
import { get as getSpilledSubstancesFromDb } from '../fieldValues/spilledSubstances/spilledSubstances';
import { get as getEmploymentTypesFromDb } from '../fieldValues/employmentTypes/employmentTypes';
import { get as getInjuryTypesFromDb } from '../fieldValues/injuryTypes/injuryTypes';
import { get as getInjuredBodyPartsFromDb } from '../fieldValues/injuredBodyParts/injuredBodyParts';
import {
  getErrorMsg,
  getErrorTransactionMsg,
  logWithRethrow,
  Operation,
} from '../helpers';
import db from '../index';
import { clear as issuesServiceClear } from './issuesService';
import { get as getLevels } from '../levels/levels';
import { wrapQuery } from '../queryWrapper';
import { get as getSites } from '../sites/sites';
import { get as getWorkTypes } from '../fieldValues/worktypes/worktypes';

import { get as getIssueWorkflows } from '../issueWorkflows/issueWorkflows';
import { get as getUsers } from 'serviceWorker/db/users/users';
import { delayCalculator } from 'shared/domain/issue/calculateDelayField';
import {
  getLastDecisionStatus,
  getLatestWorkflowDecision,
  LastDecisionStatus,
} from 'shared/domain/workflow/lastDecisionStatus';
import { WorkflowDecisionOutDto } from 'shared/domain/workflow/types';
import type { IssuesQuery } from 'shared/domain/issue/issueQuery';
import { filterByGroup, searching } from 'shared/filter/filter';
import { paginate } from 'shared/filter/paginate';
import { sorting } from 'shared/filter/sort/sort';
import { FILTER_TYPE, RelationsMap, SingleFilter } from 'shared/filter/types';
import { genRelationsMap } from 'shared/utils/relationMap';
import { sortByFractionalOrder } from 'helpers/sort';
import {
  makeDefaultCount,
  makeDefaultGetMany,
  makeDefaultGetManyByIds,
  makeDefaultGetOne,
} from '../defaultDaoFactories';

import { getIssueForm } from 'serviceWorker/services/issueForm/getIssueForm';
import { getAllCurrentVisibleFieldsPromise } from 'serviceWorker/services/visibleFields/getVisibleFieldsPromise';
import { amountPercentageCalculator } from 'shared/domain/issue/calculateAmountPecentageField';
import { ISSUE_HIDDEN_DIRECT_FILTER_PATHS } from 'shared/domain/issue/mapping/toOut/issueFieldMappingConfig';
import {
  buildIssueNumber,
  normalizeIssueNumberForSearch,
} from 'shared/domain/issue/issueNumber';
import {
  toInspectionNumberSearchFields,
  type InspectionNumberSearchFields,
} from 'shared/domain/inspection/buildInspectionNumberSearch';
import { VisibleFieldModel } from 'shared/domain/visibleField/visibleFieldModel';
import { toVisibleFieldModelMap } from 'shared/domain/visibleField/visibleFieldsMap';
import { debugLog } from 'shared/logger/debugLog';
import { Changes } from 'shared/types/commonEntities';
import { HashMap, LabelledEntity } from 'shared/types/commonView';
import { LogLevel } from 'shared/types/logger';
import { nowISO } from 'shared/utils/date/dates';
import { retry } from 'shared/utils/retry';
import { issueContract } from 'shared/domain/issue/issueContract';
import { makeClearPublisher } from 'serviceWorker/broadcasts/publishers';

async function getSiteCodeByIdMap(): Promise<Record<string, string>> {
  const sites = await getSites();
  return sites.reduce<Record<string, string>>((result, site) => {
    result[site._id] = site.code || '';
    return result;
  }, {});
}

/**
 * Builds sort labels for issue workflow stages. The label is a synthetic key
 * `${workflowName}\u0001${paddedIndex}` so that the standard string-based
 * relation sort yields: stages grouped by workflow (alphabetically), and
 * within each workflow ordered by their fractional `order` (matching the
 * workflow editor). The visible cell label is rendered separately on the UI.
 */
async function getIssueWorkflowStagesForSortFromDb(): Promise<LabelledEntity[]> {
  const workflows = await getIssueWorkflows();
  const byStageId = new Map<string, LabelledEntity>();
  for (const workflow of workflows) {
    const orderedStages = [...(workflow.stages ?? [])]
      .filter((stage) => !stage.deleted)
      .sort(sortByFractionalOrder);
    const workflowName = workflow.name ?? '';
    orderedStages.forEach((stage, index) => {
      if (byStageId.has(stage._id)) return;
      const paddedIndex = String(index).padStart(6, '0');
      byStageId.set(stage._id, {
        _id: stage._id,
        label: `${workflowName}\u0001${paddedIndex}`,
      });
    });
  }
  return Array.from(byStageId.values());
}

type IssueNumberSearchFields = {
  issueNumberSearch: string;
  issueNumberSearchNormalized: string;
};

function toIssueNumberSearchFields(issueNumberSearch: string): IssueNumberSearchFields {
  return {
    issueNumberSearch,
    issueNumberSearchNormalized: normalizeIssueNumberForSearch(issueNumberSearch),
  };
}

function toInspectionNumberSearchFieldsFromEntity(
  inspection: { _id: string; createdAt: string; template: string },
  template: { process: string }
): InspectionNumberSearchFields {
  return toInspectionNumberSearchFields(inspection, template.process);
}

const EMPTY_INSPECTION_SEARCH: InspectionNumberSearchFields = {
  inspectionNumberSearch: '',
  inspectionNumberSearchNormalized: '',
};

function issueNeedsInspectionNumberSearchBackfill(issue: IssueEntity): boolean {
  if (!issue.inspection) {
    return Boolean(
      issue.inspectionNumberSearch ||
        issue.inspectionNumberSearchNormalized
    );
  }
  return (
    !issue.inspectionNumberSearch || !issue.inspectionNumberSearchNormalized
  );
}

async function applyInspectionNumberSearchToIssueEntities(
  entities: IssueEntity[]
): Promise<void> {
  for (const entity of entities) {
    if (!entity.inspection) {
      entity.inspectionNumberSearch = '';
      entity.inspectionNumberSearchNormalized = '';
    }
  }

  const withInspection = entities.filter((e) => e.inspection);
  if (!withInspection.length) {
    return;
  }

  const inspectionIds = [
    ...new Set(
      withInspection
        .map((e) => e.inspection as string)
        .filter((id): id is string => Boolean(id))
    ),
  ];

  const inspectionRows = await db.inspections
    .where('_id')
    .anyOf(inspectionIds)
    .toArray();
  const inspectionById = Object.fromEntries(
    inspectionRows.map((row) => [row._id, row])
  );

  const templateIds = [
    ...new Set(inspectionRows.map((row) => row.template).filter(Boolean)),
  ] as string[];

  const templateRows =
    templateIds.length > 0
      ? await db.inspectionTemplates.where('_id').anyOf(templateIds).toArray()
      : [];
  const templateById = Object.fromEntries(
    templateRows.map((row) => [row._id, row])
  );

  for (const issue of withInspection) {
    const inspectionRow = issue.inspection
      ? inspectionById[issue.inspection]
      : undefined;
    const templateRow = inspectionRow
      ? templateById[inspectionRow.template]
      : undefined;

    if (!inspectionRow || !templateRow) {
      continue;
    }

    const fields = toInspectionNumberSearchFieldsFromEntity(
      inspectionRow,
      templateRow
    );
    issue.inspectionNumberSearch = fields.inspectionNumberSearch;
    issue.inspectionNumberSearchNormalized =
      fields.inspectionNumberSearchNormalized;
  }
}

async function ensureInspectionNumberSearchBackfill(
  issues: IssueEntity[]
): Promise<void> {
  const stale = issues.filter(issueNeedsInspectionNumberSearchBackfill);
  if (!stale.length) {
    return;
  }

  await applyInspectionNumberSearchToIssueEntities(stale);

  const toWrite = stale.filter((issue) => {
    if (!issue.inspection) {
      return true;
    }
    return Boolean(
      issue.inspectionNumberSearch && issue.inspectionNumberSearchNormalized
    );
  });

  if (!toWrite.length) {
    return;
  }

  const fieldsById = toWrite.reduce<
    Record<string, InspectionNumberSearchFields>
  >((acc, issue) => {
    acc[issue._id] = {
      inspectionNumberSearch: issue.inspectionNumberSearch ?? '',
      inspectionNumberSearchNormalized:
        issue.inspectionNumberSearchNormalized ?? '',
    };
    return acc;
  }, {});

  await db.issues
    .where('_id')
    .anyOf(toWrite.map((issue) => issue._id))
    .modify((issue: IssueEntity) => {
      const fields = fieldsById[issue._id];
      if (!fields) {
        return;
      }
      issue.inspectionNumberSearch = fields.inspectionNumberSearch;
      issue.inspectionNumberSearchNormalized =
        fields.inspectionNumberSearchNormalized;
    })
    .catch((e) =>
      logWithRethrow({
        logLevel: LogLevel.INFO,
        msg: getErrorMsg(Operation.updateOne, 'issues'),
        errorObj: e,
        additionalInfo: {
          query: {
            ids: toWrite.map((issue) => issue._id),
            fields: [
              'inspectionNumberSearch',
              'inspectionNumberSearchNormalized',
            ],
          },
        },
      })
    );
}

async function resolveInspectionNumberSearchOnUpdate(
  changes: Changes<IssueEntity>,
  currentIssue: IssueEntity | undefined
): Promise<InspectionNumberSearchFields | undefined> {
  const inspectionTouched = Object.prototype.hasOwnProperty.call(
    changes,
    'inspection'
  );

  const missingDenormWhileLinked =
    !inspectionTouched &&
    Boolean(currentIssue?.inspection) &&
    (!currentIssue?.inspectionNumberSearch ||
      !currentIssue?.inspectionNumberSearchNormalized);

  if (!inspectionTouched && !missingDenormWhileLinked) {
    return undefined;
  }

  const nextInspection = inspectionTouched
    ? (changes.inspection as string | undefined)
    : currentIssue?.inspection;

  if (!nextInspection) {
    return inspectionTouched ? EMPTY_INSPECTION_SEARCH : undefined;
  }

  const inspectionRow = await db.inspections.get(nextInspection);
  if (!inspectionRow) {
    return undefined;
  }

  const templateRow = await db.inspectionTemplates.get(inspectionRow.template);
  if (!templateRow) {
    return undefined;
  }

  return toInspectionNumberSearchFieldsFromEntity(
    inspectionRow,
    templateRow
  );
}

function getSiteIdFromIssueChanges(
  changes: Changes<IssueEntity>
): string | undefined {
  const primaryDataChange =
    changes.primaryData && typeof changes.primaryData === 'object'
      ? (changes.primaryData as Record<string, unknown>)
      : undefined;

  return typeof primaryDataChange?.site === 'string'
    ? primaryDataChange.site
    : undefined;
}

async function resolveIssueNumberSearchOnUpdate(
  issueId: string,
  changes: Changes<IssueEntity>,
  currentIssue: IssueEntity | undefined
): Promise<IssueNumberSearchFields | undefined> {
  const createdAtFromChange =
    typeof changes.createdAt === 'string' ? changes.createdAt : undefined;
  const processFromChange =
    typeof changes.process === 'string' ? changes.process : undefined;
  const siteIdFromChange = getSiteIdFromIssueChanges(changes);

  const nextCreatedAt = createdAtFromChange ?? currentIssue?.createdAt;
  const nextProcess = processFromChange ?? currentIssue?.process;
  const nextSiteId = siteIdFromChange ?? currentIssue?.primaryData?.site;

  const shouldRebuildIssueNumberSearchRaw =
    currentIssue?.issueNumberSearch == null ||
    createdAtFromChange !== undefined ||
    processFromChange !== undefined ||
    siteIdFromChange !== undefined;

  if (!shouldRebuildIssueNumberSearchRaw) {
    if (
      currentIssue?.issueNumberSearch &&
      !currentIssue.issueNumberSearchNormalized
    ) {
      return toIssueNumberSearchFields(currentIssue.issueNumberSearch);
    }
    return undefined;
  }

  if (!nextCreatedAt || !nextProcess || !nextSiteId) {
    return undefined;
  }

  const siteCodeById = await getSiteCodeByIdMap();
  const issueNumberSearch = buildIssueNumber({
    createdAt: nextCreatedAt,
    processId: nextProcess,
    siteCode: siteCodeById[nextSiteId] || '',
    hashtag: issueId.slice(-4),
  });
  return toIssueNumberSearchFields(issueNumberSearch);
}

async function ensureIssueNumberSearchBackfill(
  issues: Array<
    Pick<IssueEntity, '_id' | 'createdAt' | 'process' | 'primaryData'> & {
      issueNumberSearch?: string;
      issueNumberSearchNormalized?: string;
    }
  >
): Promise<void> {
  const issuesMissingSearchFields = issues.filter(
    (issue) => !issue.issueNumberSearch || !issue.issueNumberSearchNormalized
  );

  if (!issuesMissingSearchFields.length) {
    return;
  }

  const siteCodeById = await getSiteCodeByIdMap();
  const issueNumberSearchFieldsById = issuesMissingSearchFields.reduce<
    Record<string, IssueNumberSearchFields>
  >((result, issue) => {
    const issueNumberSearch =
      issue.issueNumberSearch ||
      buildIssueNumber({
        createdAt: issue.createdAt,
        processId: issue.process,
        siteCode: siteCodeById[issue.primaryData.site] || '',
        hashtag: issue._id.slice(-4),
      });

    result[issue._id] = toIssueNumberSearchFields(issueNumberSearch);
    return result;
  }, {});

  issuesMissingSearchFields.forEach((issue) => {
    const fields = issueNumberSearchFieldsById[issue._id];
    issue.issueNumberSearch = fields.issueNumberSearch;
    issue.issueNumberSearchNormalized = fields.issueNumberSearchNormalized;
  });

  await db.issues
    .where('_id')
    .anyOf(issuesMissingSearchFields.map((issue) => issue._id))
    .modify((issue: IssueEntity) => {
      const fields = issueNumberSearchFieldsById[issue._id];
      issue.issueNumberSearch = fields.issueNumberSearch;
      issue.issueNumberSearchNormalized = fields.issueNumberSearchNormalized;
    })
    .catch((e) =>
      logWithRethrow({
        logLevel: LogLevel.INFO,
        msg: getErrorMsg(Operation.updateOne, 'issues'),
        errorObj: e,
        additionalInfo: {
          query: {
            ids: issuesMissingSearchFields.map((issue) => issue._id),
            fields: ['issueNumberSearch', 'issueNumberSearchNormalized'],
          },
        },
      })
    );
}

export const clearForPull = wrapQuery(db, (): Promise<void> => {
  return db
    .transaction(
      'rw',
      db.issues,
      db.documents,
      async function transactionClearIssues(): Promise<any> {
        debugLog('flushing table:', 'issues');

        await Promise.all([
          db.issues.clear().catch((e) =>
            logWithRethrow({
              logLevel: LogLevel.INFO,
              msg: getErrorMsg(Operation.clear, 'issues'),
              errorObj: e,
              additionalInfo: null,
            })
          ),
          removeSettledDocumentsBatchByType('issueId'),
        ]);
      }
    )
    .catch((e) => {
      logWithRethrow({
        logLevel: LogLevel.INFO,
        msg: getErrorTransactionMsg(
          [Operation.clear],
          ['issues', 'documents']
        ),
        errorObj: e,
        additionalInfo: {},
      });
    });
});

export const clear = entityDbClearFactory(
  db,
  ['issues', 'issuesService', 'documents'],
  clearForPull,
  issuesServiceClear,
  makeClearPublisher(issueContract)
);

export const quantity = makeDefaultCount<IssueEntity>(db, db.issues);

export const get = makeDefaultGetMany<IssueEntity>(db, db.issues, {
  deleted: 0,
});

export const getAll = makeDefaultGetMany<IssueEntity>(db, db.issues);

export const getByInspectionId = wrapQuery(
  db,
  async (inspectionId: string): Promise<IssueEntity[]> => {
    if (!inspectionId) {
      return [];
    }
    return db.issues
      .where('inspection')
      .equals(inspectionId)
      .and((issue) => issue.deleted === 0)
      .toArray();
  }
);

export const getAllByInspectionId = wrapQuery(
  db,
  async (inspectionId: string): Promise<IssueEntity[]> => {
    if (!inspectionId) {
      return [];
    }
    return db.issues.where('inspection').equals(inspectionId).toArray();
  }
);

export const addBatch = wrapQuery(
  db,
  async (data: IssueInDto[]): Promise<IssueEntity[]> => {
    const siteCodeById = await getSiteCodeByIdMap();

    return db
      .transaction(
        'rw',
        db.issues,
        db.documents,
        db.inspections,
        db.inspectionTemplates,
        async () => {
        const documentsData = data.flatMap((issue: IssueInDto) => {
          const docs = [
            ...issue.primaryData.documents.map((document: DocumentInDto) =>
              inDtoToDocumentInsertEntity(document, {
                issueId: issue._id,
              })
            ),
            ...issue.events.flatMap((event) => {
              const eventDocs = event.documents.map(
                (document: DocumentInDto) =>
                  inDtoToDocumentInsertEntity(document, {
                    issueId: issue._id,
                    eventId: event._id,
                  })
              );
              return eventDocs;
            }),
            ...issue.incomingEmailMessages.flatMap(
              (incomingEmailMessage) => {
                const incomingEmailMessageDocs =
                  incomingEmailMessage.documents.map(
                    (document: DocumentInDto) =>
                      inDtoToDocumentInsertEntity(document, {
                        issueId: issue._id,
                        incomingEmailMessageId: incomingEmailMessage._id,
                      })
                  );
                return incomingEmailMessageDocs;
              }
            ),
          ];
          return docs;
        });

        const existingIssues = await db.issues.bulkGet(
          data.map((issue) => issue._id)
        );

        const entities = data.map((issue, index) => {
          const entity = issueDtoToEntityToSave(
            issue,
            siteCodeById[issue.primaryData.site] || ''
          ) as unknown as IssueEntity;
          const existing = existingIssues[index];

          if (existing?.linkedDocumentation?.length) {
            entity.linkedDocumentation = mergeLinkedDocumentationItems(
              existing.linkedDocumentation,
              entity.linkedDocumentation
            );
          }

          return entity;
        });

        await applyInspectionNumberSearchToIssueEntities(entities);

        await Promise.all([
          db.issues.bulkPut(entities),
          upsertDocumentsBatch(documentsData),
        ]);
        return entities;
      }
    )
      .catch((e) => {
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorTransactionMsg(
            [Operation.addBatch],
            ['issues', 'documents']
          ),
          errorObj: e,
          additionalInfo: {},
        });
      });
  }
);

export const updateBatch = addBatch;

export const removeBatch = wrapQuery(
  db,
  (ids: string[]): Promise<string[]> => {
    return db
      .transaction('rw', db.issues, db.documents, async function () {
        const deleteIssues = db.issues
          .where('_id')
          .anyOf(ids)
          .delete()
          .catch((e) =>
            logWithRethrow({
              logLevel: LogLevel.INFO,
              msg: getErrorMsg(Operation.removeBatch, 'issues'),
              errorObj: e,
              additionalInfo: { query: { ids } },
            })
          );
        const deleteDocuments = removeDocumentsBatch({ issueId: ids });

        await Promise.all([deleteIssues, deleteDocuments]);
        return ids;
      })
      .catch((e) =>
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorTransactionMsg(
            [Operation.removeBatch],
            ['issues', 'documents']
          ),
          errorObj: e,
          additionalInfo: {
            query: {
              ids,
            },
          },
        })
      );
  }
);

export const updateOne = wrapQuery(
  db,
  async (
    issueId: string,
    changes: Changes<IssueEntity>,
    options?: { skipModifyTime: boolean }
  ): Promise<any> => {
    const currentIssue = await db.issues.get(issueId);
    const issueNumberSearchFields = await resolveIssueNumberSearchOnUpdate(
      issueId,
      changes,
      currentIssue
    );

    const inspectionNumberSearchFields =
      await resolveInspectionNumberSearchOnUpdate(changes, currentIssue);

    const modifiedAt = options?.skipModifyTime
      ? {}
      : { modifiedAt: nowISO() };

    const changesWithIssueNumberSearch = {
      ...changes,
      ...(issueNumberSearchFields || {}),
      ...(inspectionNumberSearchFields || {}),
    };

    return db.issues
      .update(issueId, { ...changesWithIssueNumberSearch, ...modifiedAt })
      .catch((e) =>
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorMsg(Operation.updateOne, 'issues'),
          errorObj: e,
          additionalInfo: { query: changes },
        })
      );
  }
);

export const getOne = makeDefaultGetOne<string, IssueEntity>(
  db,
  db.issues,
  '_id'
);

export const getByIds = makeDefaultGetManyByIds<string, IssueEntity>(
  db,
  db.issues,
  '_id'
);

// Creates hashmap of related items, allowing us to sort/search by them.
export const searchIssueRelationMap = async (): Promise<RelationsMap> => {
  const { getProposedCorrectiveAction } =
    await createIssueFormFieldValueRelations();

  const getRootCauses = async (): Promise<LabelledEntity[]> =>
    getRootCausesFromDb();
  const getImpacts = async (): Promise<LabelledEntity[]> =>
    getImpactsFromDb();
  const getPriorities = async (): Promise<LabelledEntity[]> =>
    getPrioritiesFromDb();
  const getProjectStages = async (): Promise<LabelledEntity[]> =>
    getProjectStagesFromDb();
  const getEffects = async (): Promise<LabelledEntity[]> =>
    getEffectsFromDb();
  const getCircumstances = async (): Promise<LabelledEntity[]> =>
    getCircumstancesFromDb();
  const getDecisionsToImposeFine = async (): Promise<LabelledEntity[]> =>
    getDecisionsToImposeFineFromDb();
  const getSpilledSubstances = async (): Promise<LabelledEntity[]> =>
    getSpilledSubstancesFromDb();

  const relations = [
    {
      getter: getSites,
      key: 'label',
      name: 'sites',
      matches: [{ path: 'primaryData.site', type: 'direct' }],
    },
    {
      getter: getLevels,
      key: 'label',
      name: 'level',
      matches: [{ path: 'primaryData.level', type: 'direct' }],
    },
    {
      getter: getUsers,
      key: 'label',
      name: 'users',
      matches: [
        { path: 'primaryData.assignee', type: 'direct' },
        { path: 'createdBy', type: 'direct' },
        { path: 'modifiedBy', type: 'direct' },
        {
          path: 'extendedData.subcontractorRepresentative',
          type: 'direct',
        },
        {
          path: 'extendedData.supervisor',
          type: 'direct',
        },
      ],
    },
    {
      getter: getCompanies,
      key: 'longLabel',
      name: 'companies',
      matches: [
        {
          path: 'primaryData.subcontractors',
          type: 'oneOfDirect',
        },
      ],
    },
    {
      getter: getContracts,
      key: 'label',
      name: 'contractNumbers',
      matches: [
        {
          path: 'primaryData.contractNumbers',
          type: 'oneOfDirect',
        },
      ],
    },
    {
      getter: getWorkTypes,
      key: 'label',
      name: 'workTypes',
      matches: [
        {
          path: 'extendedData.workTypes',
          type: 'oneOfDirect',
        },
      ],
    },
    {
      getter: getHazardCategories,
      key: 'label',
      name: 'hazardCategories',
      matches: [
        {
          path: 'extendedData.hazardCategories',
          type: 'oneOfDirect',
        },
      ],
    },
    {
      getter: getCorrectiveActionTypes,
      key: 'label',
      name: 'correctiveActionType',
      matches: [
        {
          path: 'extendedData.proposedCorrectiveAction',
          type: 'direct',
        },
      ],
    },
    {
      getter: getEnvironmentalAspects,
      key: 'label',
      name: 'environmentalAspect',
      matches: [
        {
          path: 'extendedData.environmentalAspect',
          type: 'direct',
        },
      ],
    },
    {
      getter: getIssueVerificationItems,
      key: 'label',
      name: 'issueVerificationItems',
      matches: [
        { path: 'extendedData.issueVerificationItems', type: 'direct' },
      ],
    },
    {
      getter: getDelayReasons,
      key: 'label',
      name: 'delayReasons',
      matches: [{ path: 'extendedData.delayReasons', type: 'direct' }],
    },
    {
      getter: getRejectionReasons,
      key: 'label',
      name: 'rejectionReasons',
      matches: [{ path: 'extendedData.rejectionReasons', type: 'direct' }],
    },
    {
      getter: getRootCauses,
      key: 'label',
      name: 'rootCauses',
      matches: [
        {
          path: 'extendedData.rootCauses',
          type: 'oneOfDirect',
        },
      ],
    },
    {
      getter: getImpacts,
      key: 'label',
      name: 'impact',
      matches: [
        {
          path: 'extendedData.impact',
          type: 'direct',
        },
      ],
    },
    {
      getter: getPriorities,
      key: 'label',
      name: 'priority',
      matches: [
        {
          path: 'extendedData.priority',
          type: 'direct',
        },
      ],
    },
    {
      getter: getProjectStages,
      key: 'label',
      name: 'projectStage',
      matches: [
        {
          path: 'extendedData.projectStage',
          type: 'direct',
        },
      ],
    },
    {
      getter: getIssueWorkflowStagesForSortFromDb,
      key: 'label',
      name: 'workflowStage',
      matches: [{ path: 'stageId', type: 'direct' }],
    },
    {
      getter: getProposedCorrectiveAction,
      key: 'label',
      name: 'proposedCorrectiveAction',
      matches: [
        {
          path: 'extendedData.proposedCorrectiveAction',
          type: 'direct',
        },
      ],
    },
    {
      getter: getDecisionsToImposeFine,
      key: 'label',
      name: 'decisionToImposeFine',
      matches: [
        {
          path: 'extendedData.decisionToImposeFine',
          type: 'direct',
        },
      ],
    },
    {
      getter: getEffects,
      key: 'label',
      name: 'effect',
      matches: [
        {
          path: 'extendedData.effect',
          type: 'direct',
        },
      ],
    },
    {
      getter: getCircumstances,
      key: 'label',
      name: 'circumstances',
      matches: [
        {
          path: 'extendedData.circumstances',
          type: 'direct',
        },
      ],
    },
    {
      getter: getSpilledSubstances,
      key: 'label',
      name: 'spilledSubstance',
      matches: [
        {
          path: 'extendedData.spilledSubstance',
          type: 'direct',
        },
      ],
    },
    {
      getter: getEmploymentTypesFromDb,
      key: 'label',
      name: 'employmentType',
      matches: [
        {
          path: 'extendedData.employmentType',
          type: 'direct',
        },
      ],
    },
    {
      getter: getInjuryTypesFromDb,
      key: 'label',
      name: 'injuryTypes',
      matches: [
        {
          path: 'extendedData.injuryTypes',
          type: 'oneOfDirect',
        },
      ],
    },
    {
      getter: getInjuredBodyPartsFromDb,
      key: 'label',
      name: 'injuredBodyParts',
      matches: [
        {
          path: 'extendedData.injuredBodyParts',
          type: 'oneOfDirect',
        },
      ],
    },
  ];

  const res = await genRelationsMap(relations);
  return res;
};

export const sortIssueRelationMap = async (
  sort: string | undefined
): Promise<RelationsMap> => {
  const { getProposedCorrectiveAction } =
    await createIssueFormFieldValueRelations();

  const getImpacts = async (): Promise<LabelledEntity[]> =>
    getImpactsFromDb();
  const getPriorities = async (): Promise<LabelledEntity[]> =>
    getPrioritiesFromDb();
  const getProjectStages = async (): Promise<LabelledEntity[]> =>
    getProjectStagesFromDb();
  const getEffects = async (): Promise<LabelledEntity[]> =>
    getEffectsFromDb();
  const getCircumstances = async (): Promise<LabelledEntity[]> =>
    getCircumstancesFromDb();
  const getRootCauses = async (): Promise<LabelledEntity[]> =>
    getRootCausesFromDb();
  const getDecisionsToImposeFine = async (): Promise<LabelledEntity[]> =>
    getDecisionsToImposeFineFromDb();
  const getSpilledSubstances = async (): Promise<LabelledEntity[]> =>
    getSpilledSubstancesFromDb();
  const relations = [
    {
      getter: getSites,
      key: 'label',
      name: 'sites',
      matches: [{ path: 'primaryData.site', type: 'direct' }],
    },
    {
      getter: getLevels,
      key: 'label',
      name: 'level',
      matches: [{ path: 'primaryData.level', type: 'direct' }],
    },
    {
      getter: getUsers,
      key: 'label',
      name: 'users',
      matches: [
        { path: 'primaryData.assignee', type: 'direct' },
        { path: 'primaryData.executor', type: 'direct' },
        { path: 'createdBy', type: 'directId' },
        { path: 'modifiedBy', type: 'directId' },
        {
          path: 'extendedData.subcontractorRepresentative',
          type: 'direct',
        },
        {
          path: 'extendedData.supervisor',
          type: 'direct',
        },
      ],
    },
    {
      getter: getCorrectiveActionTypes,
      key: 'label',
      name: 'correctiveActionType',
      matches: [
        {
          path: 'extendedData.proposedCorrectiveAction',
          type: 'direct',
        },
      ],
    },
    {
      getter: getEnvironmentalAspects,
      key: 'label',
      name: 'environmentalAspect',
      matches: [
        {
          path: 'extendedData.environmentalAspect',
          type: 'direct',
        },
      ],
    },

    {
      getter: getImpacts,
      key: 'label',
      name: 'impact',
      matches: [
        {
          path: 'extendedData.impact',
          type: 'direct',
        },
      ],
    },
    {
      getter: getPriorities,
      key: 'label',
      name: 'priority',
      matches: [
        {
          path: 'extendedData.priority',
          type: 'direct',
        },
      ],
    },
    {
      getter: getProjectStages,
      key: 'label',
      name: 'projectStage',
      matches: [
        {
          path: 'extendedData.projectStage',
          type: 'direct',
        },
      ],
    },
    {
      getter: getIssueWorkflowStagesForSortFromDb,
      key: 'label',
      name: 'workflowStage',
      matches: [{ path: 'stageId', type: 'direct' }],
    },
    {
      getter: getProposedCorrectiveAction,
      key: 'label',
      name: 'proposedCorrectiveAction',
      matches: [
        {
          path: 'extendedData.proposedCorrectiveAction',
          type: 'direct',
        },
      ],
    },
    {
      getter: getDecisionsToImposeFine,
      key: 'label',
      name: 'decisionToImposeFine',
      matches: [
        {
          path: 'extendedData.decisionToImposeFine',
          type: 'direct',
        },
      ],
    },
    {
      getter: getEffects,
      key: 'label',
      name: 'effect',
      matches: [
        {
          path: 'extendedData.effect',
          type: 'direct',
        },
      ],
    },
    {
      getter: getCircumstances,
      key: 'label',
      name: 'circumstances',
      matches: [
        {
          path: 'extendedData.circumstances',
          type: 'direct',
        },
      ],
    },
    {
      getter: getSpilledSubstances,
      key: 'label',
      name: 'spilledSubstance',
      matches: [
        {
          path: 'extendedData.spilledSubstance',
          type: 'direct',
        },
      ],
    },
    {
      getter: getEmploymentTypesFromDb,
      key: 'label',
      name: 'employmentType',
      matches: [
        {
          path: 'extendedData.employmentType',
          type: 'direct',
        },
      ],
    },
    {
      getter: getInjuryTypesFromDb,
      key: 'label',
      name: 'injuryTypes',
      matches: [
        {
          path: 'extendedData.injuryTypes',
          type: 'oneOfDirect',
        },
      ],
    },
    {
      getter: getInjuredBodyPartsFromDb,
      key: 'label',
      name: 'injuredBodyParts',
      matches: [
        {
          path: 'extendedData.injuredBodyParts',
          type: 'oneOfDirect',
        },
      ],
    },
  ];

  const sortMatch = relations.some((relation) => {
    return relation.matches.some((relationMatch) => {
      return relationMatch.path === sort;
    });
  });

  if (!sortMatch) {
    return Promise.resolve({} as RelationsMap);
  }

  const res = await genRelationsMap(relations);
  return res;
};

const hashMapByPath = (
  relations: RelationsMap,
  path: string | undefined
): null | Record<string, string> => {
  if (!path || !relations.matches) {
    return null;
  }
  const match = relations.matches.find((relation) => {
    const isMatch = relation.matches.find((match) => match.path === path);
    return isMatch;
  });

  if (!match) {
    return null;
  }

  const result = relations.hashMap[match.name];
  return result;
};

/** The page of issues matching the query (filter, search, sort, archived) and how many match in total. */
export const getFiltered = async ({
  filter,
  search,
  sort,
  pagination,
  archived,
  timezone,
}: IssuesQuery): Promise<{ items: IssueEntity[]; total: number }> => {
  const issues = Array.isArray(archived)
    ? await getAll()
    : await get({ deleted: archived ? 1 : 0 });
  await ensureIssueNumberSearchBackfill(issues);
  await ensureInspectionNumberSearchBackfill(issues);
  const searchKey = search.find((elem) => elem.type === 'search')?.value;
  const relations = searchKey
    ? await searchIssueRelationMap()
    : await sortIssueRelationMap(sort?.path);

  const visibleFieldsMap = await createVisibleFieldsMap();
  await addVirtualFields(issues, timezone);
  await addLastDecisionStatusField(issues);

  const filtered = filterByGroup({
    set: issues,
    group: filter,
    skip: makeSkipNotVisibleFields(visibleFieldsMap),
  });
  const searched = await searching({
    set: filtered,
    filters: search,
    relationsMap: relations,
  });
  const sortRelation = hashMapByPath(relations, sort?.path);
  const sorted = sorting(searched, sort, sortRelation);
  // TODO: store above result in some temporary cache, when user asks for the same filter/search string
  // but different page (should be common case) we could skip all logic and just paginate
  const paginated = paginate(sorted, pagination);
  return { items: paginated as IssueEntity[], total: searched.length };
};

export async function getByInspectionForView(
  inspectionId: string,
  timezone: string
): Promise<{ items: IssueEntity[]; total: number }> {
  // Includes archived (`deleted: 1`) on purpose: parity with `getFiltered` for
  // `archived: [true, false]`, which reads `getAll()`. `getByInspectionId`
  // (active only) would drop archived issues from the inspection's cards.
  const issues = await getAllByInspectionId(inspectionId);
  await ensureIssueNumberSearchBackfill(issues);
  await ensureInspectionNumberSearchBackfill(issues);
  await addVirtualFields(issues, timezone);
  await addLastDecisionStatusField(issues);
  return { items: issues, total: issues.length };
}

async function addVirtualFields(
  issues: {
    extendedData: {
      targetCompletionDate?: string;
      finalCompletionDate?: string;
      completionDateDelay?: number | null;
      startDateDelay?: number | null;
      targetStartDate?: string;
      finalStartDate?: string;
      targetAmount?: number;
      completedAmount?: number | null;
      amountPercentage?: number | null;
      targetResponseToAuthorDate?: string;
      finalResponseToAuthorDate?: string;
      responseToAuthorDelay?: number | null;
    };
    hashtag?: string | undefined;
    _id: string;
  }[],
  timezone: string
): Promise<void> {
  issues.forEach((issue) => {
    const {
      targetCompletionDate,
      finalCompletionDate,
      targetStartDate,
      finalStartDate,
      targetAmount,
      completedAmount,
      targetResponseToAuthorDate,
      finalResponseToAuthorDate,
    } = issue.extendedData;
    const completionDelay = delayCalculator.execute(
      targetCompletionDate,
      finalCompletionDate,
      timezone
    );
    const startDelay = delayCalculator.execute(
      targetStartDate,
      finalStartDate,
      timezone
    );
    const amountPercentage = amountPercentageCalculator.execute(
      targetAmount,
      completedAmount
    );
    const responseToAuthorDelay = delayCalculator.execute(
      targetResponseToAuthorDate,
      finalResponseToAuthorDate,
      timezone
    );
    issue.extendedData.completionDateDelay = completionDelay;
    issue.extendedData.startDateDelay = startDelay;
    issue.extendedData.amountPercentage = amountPercentage;
    issue.extendedData.responseToAuthorDelay = responseToAuthorDelay;
    issue.hashtag = issue._id.slice(-4);
  });
}

async function addLastDecisionStatusField(
  issues: {
    workflowId?: string;
    workflowDecisions?: WorkflowDecisionOutDto[];
    lastDecisionStatus?: LastDecisionStatus | null;
    latestDecisionId?: string | null;
  }[]
): Promise<void> {
  const workflows = await getIssueWorkflows();
  const workflowsById = new Map(
    workflows.map((workflow) => [workflow._id, workflow])
  );

  issues.forEach((issue) => {
    const workflow = issue.workflowId
      ? workflowsById.get(issue.workflowId)
      : undefined;
    const latestDecision = getLatestWorkflowDecision(issue.workflowDecisions);
    issue.lastDecisionStatus = getLastDecisionStatus(issue, workflow, latestDecision);
    issue.latestDecisionId = latestDecision?._id ?? null;
  });
}

/** Every issue matching the query, as its id and site (previous / next navigation). */
export const getFilteredIdList = (query: IssuesQuery): Promise<{ _id: string; site: string }[]> =>
  getFiltered(query).then(({ items }) => items.map(({ _id, primaryData }) => ({ _id, site: primaryData.site })));

async function createIssueFormFieldValueRelations(): Promise<{
  getProposedCorrectiveAction: () => Promise<LabelledEntity[]>;
}> {
  const issueForms = await retry(() => getIssueForm(), 20);

  const proposedCorrectiveActionField: { items?: any[] } = !issueForms
    ? { items: [] }
    : issueForms.find(
        (field: { name: string; items?: any[] }) =>
          field.name === 'proposedCorrectiveAction'
      )!;

  const getProposedCorrectiveAction = (): Promise<any[]> =>
    Promise.resolve(proposedCorrectiveActionField.items!);

  return {
    getProposedCorrectiveAction,
  };
}

function makeSkipNotVisibleFields(
  visibleFields: HashMap<HashMap<boolean>>
): (issue: IssueEntity, filter: SingleFilter) => boolean | 'exclude' {
  const shouldExcludeWhenFieldNotVisible = (filter: SingleFilter): boolean => {
    if (
      filter.type === FILTER_TYPE.dateRange ||
      filter.type === FILTER_TYPE.oneOfDirect
    ) {
      return true;
    }

    if (filter.type === FILTER_TYPE.direct) {
      return ISSUE_HIDDEN_DIRECT_FILTER_PATHS.has(filter.path);
    }

    return false;
  };

  return function skipNotVisibleFields(
    issue: IssueEntity,
    filter: SingleFilter
  ): boolean | 'exclude' {
    const visibleFieldsOnProcess = visibleFields[issue.process];
    if (!visibleFieldsOnProcess) {
      return shouldExcludeWhenFieldNotVisible(filter) ? 'exclude' : false;
    }

    const fieldName = extractFieldName(filter.path);
    if (!(fieldName in visibleFieldsOnProcess)) {
      return false;
    }

    const isFieldVisible = Boolean(visibleFieldsOnProcess[fieldName]);

    if (isFieldVisible) {
      return false;
    }

    return shouldExcludeWhenFieldNotVisible(filter)
      ? 'exclude'
      : true;
  };
}

function extractFieldName(path: string): string {
  const [fieldTypeOrName, fieldName] = path.split('.');
  return fieldName || fieldTypeOrName;
}

async function createVisibleFieldsMap(): Promise<
  HashMap<HashMap<boolean>>
> {
  const visibleFields = await getAllCurrentVisibleFieldsPromise();

  const mappedByProcess = visibleFields.reduce(
    (result, visibleField: VisibleFieldModel) => {
      if (!result[visibleField.processId]) {
        result[visibleField.processId] = [];
      }
      result[visibleField.processId].push(visibleField);
      return result;
    },
    {} as HashMap<VisibleFieldModel[]>
  );

  return addNonFieldFilters(toVisibleFieldModelMap(mappedByProcess));
}

function addNonFieldFilters(
  visibleFieldsMap: HashMap<HashMap<boolean>>
): HashMap<HashMap<boolean>> {
  Object.keys(visibleFieldsMap).forEach((key) => {
    const fieldsOnProcess = visibleFieldsMap[key];
    fieldsOnProcess.process = true;
    fieldsOnProcess.inspection = true;
    fieldsOnProcess.hashtag = true;
  });

  return visibleFieldsMap;
}
